import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Users,
  Trophy,
  Clock,
  CalendarBlank,
  Download,
  ArrowRight,
  Warning,
} from "phosphor-react";
import { formatCurrency } from "../utils/formatters";
import { format, subDays, parseISO, differenceInDays } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReportsCustomers() {
  const navigate = useNavigate();
  const [topCustomers, setTopCustomers] = useState(null);
  const [inactiveCustomers, setInactiveCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [daysInactive, setDaysInactive] = useState(90);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });

  const fetchTopCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const startISO = new Date(dateRange.start).toISOString();
      const endISO = new Date(dateRange.end + "T23:59:59").toISOString();
      
      const response = await fetch(
        `${API}/reports/top-customers?start_date=${startISO}&end_date=${endISO}&limit=10`,
        { credentials: "include" }
      );
      
      if (response.ok) setTopCustomers(await response.json());
    } catch (error) {
      console.error("Error fetching top customers:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchInactiveCustomers = useCallback(async () => {
    setInactiveLoading(true);
    try {
      const response = await fetch(
        `${API}/reports/inactive-customers?days_inactive=${daysInactive}&limit=20`,
        { credentials: "include" }
      );
      
      if (response.ok) setInactiveCustomers(await response.json());
    } catch (error) {
      console.error("Error fetching inactive customers:", error);
    } finally {
      setInactiveLoading(false);
    }
  }, [daysInactive]);

  useEffect(() => {
    fetchTopCustomers();
  }, [fetchTopCustomers]);

  useEffect(() => {
    fetchInactiveCustomers();
  }, [fetchInactiveCustomers]);

  const exportTopCustomersCSV = () => {
    if (!topCustomers?.top_customers?.length) return;
    
    const headers = ["Customer", "Email", "Phone", "Revenue", "Invoices", "Last Service"];
    const rows = topCustomers.top_customers.map((cust) => [
      cust.name,
      cust.email,
      cust.phone,
      cust.total_revenue,
      cust.invoice_count,
      cust.last_service_date ? format(parseISO(cust.last_service_date), "MM/dd/yyyy") : "Never",
    ]);
    
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `top-customers-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  const exportInactiveCSV = () => {
    if (!inactiveCustomers?.customers?.length) return;
    
    const headers = ["Customer", "Email", "Phone", "Last Service", "Days Since"];
    const rows = inactiveCustomers.customers.map((cust) => [
      cust.name,
      cust.email,
      cust.phone,
      cust.last_service_date ? format(parseISO(cust.last_service_date), "MM/dd/yyyy") : "Never",
      cust.days_since_service ?? "N/A",
    ]);
    
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inactive-customers-${daysInactive}days-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  if (loading && !topCustomers) {
    return (
      <div className="space-y-6" data-testid="reports-customers-page">
        <div className="animate-pulse">
          <div className="h-8 bg-zinc-200 rounded w-48 mb-6" />
          <div className="h-64 bg-zinc-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} data-testid="back-to-reports">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Customer Reports</h1>
            <p className="text-sm text-zinc-500">Top customers and inactive customer tracking</p>
          </div>
        </div>
      </div>

      {/* Top Customers */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-100 flex items-center justify-center rounded">
                <Trophy size={18} className="text-amber-600" weight="bold" />
              </div>
              <CardTitle className="font-heading text-lg">Top Customers by Revenue</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2">
                <CalendarBlank size={16} className="text-zinc-500" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                  className="bg-transparent border-none text-sm focus:outline-none w-28"
                />
                <span className="text-zinc-400">-</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                  className="bg-transparent border-none text-sm focus:outline-none w-28"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportTopCustomersCSV}
                data-testid="export-top-customers-csv"
              >
                <Download size={14} className="mr-1" /> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {topCustomers?.top_customers?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-customers-table">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 uppercase border-b border-zinc-200">
                    <th className="pb-3 font-medium">#</th>
                    <th className="pb-3 font-medium">Customer</th>
                    <th className="pb-3 font-medium text-right">Revenue</th>
                    <th className="pb-3 font-medium text-center">Invoices</th>
                    <th className="pb-3 font-medium">Last Service</th>
                    <th className="pb-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.top_customers.map((cust, idx) => (
                    <tr key={cust.customer_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? "bg-amber-400 text-white" :
                          idx === 1 ? "bg-zinc-300 text-zinc-700" :
                          idx === 2 ? "bg-amber-600 text-white" :
                          "bg-zinc-100 text-zinc-600"
                        }`}>
                          {idx + 1}
                        </div>
                      </td>
                      <td className="py-3">
                        <div>
                          <p className="font-medium">{cust.name}</p>
                          <p className="text-xs text-zinc-500">{cust.email || cust.phone || "-"}</p>
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <span className="font-heading font-bold text-green-600">
                          {formatCurrency(cust.total_revenue)}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <Badge variant="secondary">{cust.invoice_count}</Badge>
                      </td>
                      <td className="py-3 text-zinc-500">
                        {cust.last_service_date 
                          ? format(parseISO(cust.last_service_date), "MMM d, yyyy")
                          : <span className="text-zinc-400">Never</span>
                        }
                      </td>
                      <td className="py-3">
                        <Link 
                          to={`/customers/${cust.customer_id}`}
                          className="text-blue-600 hover:text-blue-700 text-xs inline-flex items-center gap-1"
                        >
                          View <ArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <Trophy size={32} className="mx-auto mb-2 opacity-50" />
              <p>No customer revenue data for this period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inactive Customers */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-100 flex items-center justify-center rounded">
                <Clock size={18} className="text-red-600" weight="bold" />
              </div>
              <CardTitle className="font-heading text-lg">Inactive Customers</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={daysInactive}
                onChange={(e) => setDaysInactive(Number(e.target.value))}
                className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm"
                data-testid="inactive-days-select"
              >
                <option value={30}>30+ days</option>
                <option value={60}>60+ days</option>
                <option value={90}>90+ days</option>
                <option value={180}>180+ days</option>
                <option value={365}>1+ year</option>
              </select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportInactiveCSV}
                data-testid="export-inactive-csv"
              >
                <Download size={14} className="mr-1" /> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary badges */}
          {inactiveCustomers?.summary && (
            <div className="flex gap-3 mb-4">
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                {inactiveCustomers.summary.total_inactive} inactive
              </Badge>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                {inactiveCustomers.summary.never_serviced} never serviced
              </Badge>
            </div>
          )}
          
          {inactiveLoading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-zinc-100 rounded" />
              ))}
            </div>
          ) : inactiveCustomers?.customers?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inactive-customers-table">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 uppercase border-b border-zinc-200">
                    <th className="pb-3 font-medium">Customer</th>
                    <th className="pb-3 font-medium">Contact</th>
                    <th className="pb-3 font-medium">Last Service</th>
                    <th className="pb-3 font-medium text-right">Days Since</th>
                    <th className="pb-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveCustomers.customers.map((cust) => (
                    <tr key={cust.customer_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3">
                        <p className="font-medium">{cust.name}</p>
                      </td>
                      <td className="py-3">
                        <p className="text-zinc-600">{cust.email || cust.phone || "-"}</p>
                      </td>
                      <td className="py-3">
                        {cust.last_service_date ? (
                          <span className="text-zinc-600">
                            {format(parseISO(cust.last_service_date), "MMM d, yyyy")}
                          </span>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Never
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {cust.days_since_service !== null ? (
                          <span className={`font-medium ${
                            cust.days_since_service > 180 ? "text-red-600" :
                            cust.days_since_service > 90 ? "text-amber-600" :
                            "text-zinc-600"
                          }`}>
                            {cust.days_since_service}d
                          </span>
                        ) : (
                          <span className="text-zinc-400">-</span>
                        )}
                      </td>
                      <td className="py-3">
                        <Link 
                          to={`/customers/${cust.customer_id}`}
                          className="text-blue-600 hover:text-blue-700 text-xs inline-flex items-center gap-1"
                        >
                          View <ArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <Users size={32} className="mx-auto mb-2 opacity-50" />
              <p>No inactive customers found</p>
            </div>
          )}
          
          {inactiveCustomers?.has_more && (
            <div className="mt-4 pt-4 border-t border-zinc-200 text-center">
              <p className="text-sm text-zinc-500">
                Showing {inactiveCustomers.customers.length} of {inactiveCustomers.summary.total_inactive} inactive customers
              </p>
              <Button variant="link" size="sm" onClick={exportInactiveCSV}>
                Export all to CSV
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
